import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { addTab, removeTab, setActiveTab } from "../../../redux/slices/NotificationCenterSlice";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";

// Components
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import ShipmentNotificationTable from "./TabContent/ShipmentNotification";
import InvoiceNotificationTable from "./TabContent/InvoiceNotification";

const NotificationDetails = () => {
  const dispatch = useDispatch();
  const { tabs, activeTab, defaultTabsCount } = useSelector(selectModuleTabs("notificationCenter"));

  const [totalNotifications, setTotalNotifications] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(false);
  const [filter, setFilter] = useState(null);

  const handleFilter = (selectedValue) => {
    setFilter(selectedValue === "All Statuses" ? null : selectedValue);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilter(null);
    setSearchTrigger((prev) => !prev);
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    const newTabId = `search-${Date.now()}`;
    dispatch(addTab({
      id: newTabId,
      name: `Search: ${searchQuery}`,
      contentType: "searchResults",
      searchQuery,
      isDynamic: true,
      createdAt: Date.now(),
    }));
    dispatch(setActiveTab({ index: tabs.length }));
  };

  const handleCreateNotification = () => {
    dispatch(addTab());
  };

  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ tabIndex: index }));
  };

  const defaultTabContents = {
    "shipment-notifications": (
      <ShipmentNotificationTable
        key="shipment-notifications"
        searchQuery={searchQuery}
        searchTrigger={searchTrigger}
        setTotalNotifications={setTotalNotifications}
      />
    ),
    "invoice-notifications": (
      <InvoiceNotificationTable
        key="invoice-notifications"
        searchQuery={searchQuery}
        searchTrigger={searchTrigger}
        setTotalNotifications={setTotalNotifications}
      />
    ),
  };

  return (
    <div className="p-fluid-1 shadow-md flex flex-col flex-1 h-full min-h-0 overflow-hidden">
      {/* Header bar */}
      <div className="flex flex-wrap justify-between items-center rounded-md p-fluid-1">
        {/* Left: Filter + Search */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton text="Filter" backgroundColor="white" textColor="black" />
          <FilterButton text="Status" backgroundColor="white" textColor="black" />

          <div className="relative flex items-center border border-gray-300 bg-white rounded-full px-3 py-1.5 w-80">
            <input
              type="text"
              placeholder="Search notifications"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              className="w-full outline-none text-fluid-sm text-gray-800"
            />
            <button
              onClick={handleSearch}
              className="absolute right-3 text-gray-500 hover:text-gray-700"
            >
              <Search size={18} strokeWidth={2} />
            </button>
          </div>

          <span className="text-gray-500 text-fluid-sm">
            {totalNotifications} notifications
          </span>
        </div>

        {/* Right: Add Notification */}
        {/* <div className="flex items-center gap-2">
          <Button
            text="Add Notification"
            onClick={handleCreateNotification}
            backgroundColor="#223688"
            textColor="white"
            fontSize="14px"
            padding="10px 18px"
            borderRadius="24px"
          />
        </div> */}
      </div>

      {/* Tab content area */}
      <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
        <Tabs
          key={tabs.length}
          tabs={tabs.map((tab) => tab.name)}
          tabContents={tabs.map((tab) => (
            defaultTabContents[tab.id] || (
              <div className="text-gray-600 text-sm p-4">Empty Tab</div>
            )
          ))}
          activeTab={activeTab}
          onTabChange={(index) => dispatch(setActiveTab({ index }))}
          isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
          onRemoveTab={handleRemoveTab}
        />
      </div>
    </div>
  );
};

export default NotificationDetails;
